import { Tooltip } from '@app/components/tooltip';
import type { PublishToEventItem } from '@app/routes/content/domain';
import { hasEventEnded } from '@app/routes/organization/routes/events/utils';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { memo } from 'react';

import { TearOutWindow } from '../icons';
import OrangeBanner from './assets/empty-orange-banner.jpg';
import { Checkbox } from './checkbox';
import { getEventPartOption, optionToEventPart } from './constants';
import { EventPartsCombobox } from './event-parts-dropdown';
import type { DraftEventPart, EventPart, EventPartOption } from './types';

type EventItemProps = {
  event: PublishToEventItem;
  eventPartOptions: EventPartOption[];
  hasEventPartValidationError: boolean;
  hasFailedEventParts: boolean;
  isDisabled: boolean;
  isFetchingEventParts: boolean;
  isSelected: boolean;
  tooltipLabel?: string;
  onEventPartChange: (eventId: string, eventPart: EventPart) => void;
  onEventPartsOpen: (eventId: string) => void;
  onRetryEventParts: (eventId: string) => void;
  onSelect: (event: PublishToEventItem) => void;
  eventPreviewUrl?: string;
  selectedEventPart?: DraftEventPart;
};

export const EventItem = memo(function EventItem({
  event,
  eventPartOptions,
  hasEventPartValidationError,
  hasFailedEventParts,
  isDisabled,
  isFetchingEventParts,
  isSelected,
  tooltipLabel,
  onEventPartChange,
  onEventPartsOpen,
  onRetryEventParts,
  onSelect,
  eventPreviewUrl,
  selectedEventPart,
}: EventItemProps) {
  const { t, i18n } = useTranslation();

  const isEventEnded = event
    ? hasEventEnded({
        expired: event.expired,
        status: event.status ?? '',
        timeEnd: event.timeEnd ?? '',
      })
    : false;

  const pattern = event.isLite ? 'webinar' : event.venueType;
  const shouldShowEventPartControls = isSelected && !isDisabled;
  const shouldShowEventPartLoading =
    shouldShowEventPartControls &&
    isFetchingEventParts &&
    eventPartOptions.length === 0;
  const shouldShowEventPartDropdown =
    shouldShowEventPartControls &&
    !hasFailedEventParts &&
    eventPartOptions.length > 0;

  return (
    <Tooltip
      tooltip={
        tooltipLabel ?? t('content-recording.associated-recording-event')
      }
      placement="top-center"
      shouldShow={isDisabled}
    >
      <div className={cx({ 'cursor-not-allowed': isDisabled })}>
        <div className="flex items-center gap-3">
          {isDisabled ? (
            <div className="h-5 w-5 shrink-0" aria-hidden />
          ) : (
            <Checkbox
              checked={isSelected}
              aria-disabled={isFetchingEventParts}
              name={event.id}
              aria-label={`Select ${event.name ?? 'event'}`}
              onChange={() => {
                if (isFetchingEventParts) return;

                onSelect(event);
              }}
            />
          )}

          <img
            src={event.image?.url ?? OrangeBanner}
            alt=""
            draggable="false"
            className={cx(
              'w-14 h-8 rounded-lg shrink-0 object-cover bg-white overflow-hidden',
              {
                'opacity-50': isDisabled,
              },
            )}
          />

          <div className="flex min-w-0 flex-1 flex-col">
            <span
              className={cx(
                'line-clamp-1 text-sm leading-[21px] font-semibold',
                {
                  'text-spring-neutral-b3': isDisabled,
                },
              )}
              title={event.name}
            >
              {event.name}
            </span>

            <div className="flex items-center gap-2">
              {event.timeStart && event.timeEnd && (
                <div className="flex">
                  <span
                    className={cx(
                      'line-clamp-1 select-none text-[12px] font-medium leading-[17px]',
                      {
                        'text-spring-neutral-b4-muted': isDisabled,
                        'text-spring-neutral-b3': !isDisabled,
                      },
                    )}
                  >
                    {new Date(event.timeStart).toLocaleDateString(
                      i18n.language,
                      {
                        day: 'numeric',
                        month: 'short',
                      },
                    )}{' '}
                    &rarr;{' '}
                    {new Date(event.timeEnd).toLocaleString(i18n.language, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}

              <span
                className={cx('inline-flex p-1 rounded select-none', {
                  'bg-[#0EA5E933] text-[#7DD3FC]': pattern === 'webinar',
                  'bg-[#FE862433] text-[#FFA366]': pattern === 'virtual',
                  'bg-[#A78BFA33] text-[#C4B5FD]': pattern === 'hybrid',
                  'bg-[#22C55E33] text-[#86EFAC]': pattern === 'onsite',
                })}
              >
                <span className="text-xxs leading-[10px] font-medium">
                  {t(`content-recording.venue-type.${pattern}`)}
                </span>
              </span>

              {isEventEnded && (
                <div className="flex items-center gap-1">
                  <div
                    className={cx('inline-block w-1.5 h-1.5 rounded-full', {
                      'bg-gray-600': isDisabled,
                      'bg-gray-500': !isDisabled,
                    })}
                  ></div>
                  <span
                    className={cx(
                      'select-none text-xs leading-[17px] font-medium inline-block',
                      {
                        'text-spring-neutral-b4-muted': isDisabled,
                        'text-spring-neutral-b3': !isDisabled,
                      },
                    )}
                  >
                    {t('content-recording.event-ended')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {eventPreviewUrl && (
            <Tooltip
              tooltip={t('content-recording.preview-event-replay')}
              placement="top-end"
            >
              <a
                href={eventPreviewUrl}
                aria-label={t('content-recording.preview-event-replay')}
                className={cx(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-10 text-spring-neutral-b2',
                  'hover:bg-gray-700 hover:text-white',
                )}
                target="_blank"
                rel="noreferrer"
              >
                <TearOutWindow className="h-4 w-4" aria-hidden="true" />
              </a>
            </Tooltip>
          )}
        </div>

        {(shouldShowEventPartLoading ||
          shouldShowEventPartDropdown ||
          hasFailedEventParts) && (
          <div className="mt-2 flex flex-col gap-1">
            {shouldShowEventPartLoading && (
              <div
                aria-hidden
                className="h-[46px] w-full animate-pulse rounded-10 border border-gray-700 bg-gray-600"
              />
            )}

            {shouldShowEventPartDropdown && (
              <>
                <EventPartsCombobox
                  hasError={hasEventPartValidationError}
                  isLoadingOptions={isFetchingEventParts}
                  onChange={option =>
                    onEventPartChange(event.id, optionToEventPart(option))
                  }
                  onOpen={() => onEventPartsOpen(event.id)}
                  options={eventPartOptions}
                  placeholder={t('content-recording.select-replay-area')}
                  value={getEventPartOption(
                    eventPartOptions,
                    selectedEventPart,
                  )}
                />

                {hasEventPartValidationError && (
                  <span className="text-xs font-medium text-[#F27676]">
                    {t('content-recording.select-replay-area-error')}
                  </span>
                )}
              </>
            )}

            {hasFailedEventParts && (
              <div className="mt-1 mx-2 flex items-center justify-between gap-3 text-xs">
                <span
                  className="line-clamp-1 select-none text-spring-neutral-b3 text-[12px] font-medium leading-[17px]"
                  title={t('content-recording.failed-to-load-event-areas')}
                >
                  {t('content-recording.failed-to-load-event-areas')}
                </span>

                <button
                  type="button"
                  className={cx(
                    'font-medium text-blue-200 rounded-10 px-2 py-1',
                    'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
                  )}
                  onClick={() => onRetryEventParts(event.id)}
                >
                  {t('content-recording.retry')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Tooltip>
  );
});
